import { useEffect, useState } from 'react';

type ResponsiveCharLimitConfig = {
  desktop: number;
  mobile: number;
  breakpoint?: number;
};

/**
 * Custom hook to get responsive character limits based on viewport width
 * Useful for truncating text in responsive components
 *
 * @param mobile - Character limit for mobile devices
 * @param desktop - Character limit for desktop devices
 * @param breakpoint - Breakpoint width in pixels (default: 1024)
 * @returns Current character limit based on viewport width
 *
 * @example
 * const charLimit = useResponsiveCharLimit({
 *   mobile: 250,
 *   desktop: 300,
 * });
 */
export const useResponsiveCharLimit = ({
  breakpoint = 1024,
  desktop,
  mobile,
}: ResponsiveCharLimitConfig): number => {
  const [charLimit, setCharLimit] = useState(mobile);

  useEffect(() => {
    const handleResize = () => {
      setCharLimit(window.innerWidth >= breakpoint ? desktop : mobile);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [mobile, desktop, breakpoint]);

  return charLimit;
};
